import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { shopifyApi } from '@/lib/api/services/shopify';
import { toast } from '@/hooks/use-toast';
import { friendlyMessage, showApiError } from '@/lib/errors';
import { showListSkeleton, useFirstLoad } from '@/lib/loadingState';
import { Search, X, Loader2, Tag, Plus, Trash2, Star } from 'lucide-react';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { useChat } from '@/contexts/ChatContext';
import { useUrlParams } from '@/hooks/useUrlState';

interface ShopifyOrderTag {
    id: string;
    tag_name: string;
    is_default: boolean;
    shopify_shop: string;
}

interface ShopifyOrdersProps {
    selectedAccountId: string | null;
    isConnected: boolean;
    shopCurrency?: string;
    shopDomain?: string;
    refreshKey?: number;
}

export function ShopifyOrders({ selectedAccountId, isConnected, shopCurrency = 'USD', shopDomain, refreshKey = 0 }: ShopifyOrdersProps) {
    const scopeId = selectedAccountId;
    const { selectedAccountId: socketBusinessAccountId } = useBusinessAccountContext();
    const { socket, isConnected: socketConnected } = useChat();
    const [localOrders, setLocalOrders] = useState<any[]>([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [loading, setLoading] = useState(false);
    const { hasLoaded, markLoaded } = useFirstLoad();
    const [error, setError] = useState<string | null>(null);
    const [fetchKey, setFetchKey] = useState(0);

    const [searchParams] = useSearchParams();
    const { set: setUrl } = useUrlParams();

    const ordersSearch = searchParams.get('shopOrdSearch') ?? '';
    const ordersFinancialStatusFilter = searchParams.get('shopOrdFinancial') ?? 'all';
    const ordersFulfillmentStatusFilter = searchParams.get('shopOrdFulfillment') ?? 'all';
    const ordersTagFilter = searchParams.get('shopOrdTag') ?? 'all';
    const ordersSortBy = searchParams.get('shopOrdSortBy') ?? 'shopify_created_at';
    const ordersSortOrder = (searchParams.get('shopOrdSortOrder') ?? 'desc') as 'asc' | 'desc';
    const ordersPage = parseInt(searchParams.get('shopOrdPage') ?? '1', 10);
    const ordersPageSize = parseInt(searchParams.get('shopOrdPageSize') ?? '10', 10);

    const [searchInput, setSearchInput] = useState(ordersSearch);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        return () => {
            if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        };
    }, []);

    useEffect(() => {
        if (!searchDebounceRef.current) {
            setSearchInput(ordersSearch);
        }
    }, [ordersSearch]);

    const setFilter = useCallback((key: string, value: string | null, resetPage = true) => {
        const patch: Record<string, string> = { [key]: (!value || value === 'all') ? '' : value };
        if (resetPage) patch.shopOrdPage = '';
        setUrl(patch, { replace: true });
    }, [setUrl]);

    const handleSearchChange = useCallback((value: string) => {
        setSearchInput(value);
        if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            searchDebounceRef.current = null;
            setUrl({ shopOrdSearch: value, shopOrdPage: '' }, { replace: true });
        }, 400);
    }, [setUrl]);

    const [orderTags, setOrderTags] = useState<ShopifyOrderTag[]>([]);
    const [tagsLoading, setTagsLoading] = useState(false);
    const [showTagDialog, setShowTagDialog] = useState(false);
    const [newTagName, setNewTagName] = useState('');
    const [newTagIsDefault, setNewTagIsDefault] = useState(false);
    const [createTagLoading, setCreateTagLoading] = useState(false);
    const [resolvedShop, setResolvedShop] = useState<string | null>(null);
    const [updatingTagForOrder, setUpdatingTagForOrder] = useState<string | null>(null);
    const [settingDefaultTag, setSettingDefaultTag] = useState<string | null>(null);

    const tagsByDomain = useMemo(() => {
        const map = new Map<string, ShopifyOrderTag[]>();
        for (const tag of orderTags) {
            const domain = tag.shopify_shop || 'unknown';
            if (!map.has(domain)) map.set(domain, []);
            map.get(domain)!.push(tag);
        }
        return map;
    }, [orderTags]);

    const activeDomain = resolvedShop || shopDomain || (orderTags.length > 0 ? orderTags[0].shopify_shop : null);

    const activeDomainTags = useMemo(
        () => activeDomain ? (tagsByDomain.get(activeDomain) || []) : orderTags,
        [tagsByDomain, activeDomain, orderTags]
    );

    const loadOrderTags = async () => {
        if (!scopeId) return;
        setTagsLoading(true);
        try {
            const res = await shopifyApi.listOrderTags(scopeId);
            setOrderTags(res.tags || []);
            if (res.shop) setResolvedShop(res.shop);
        } catch {
        } finally {
            setTagsLoading(false);
        }
    };

    useEffect(() => {
        const targetId = scopeId;
        if (!isConnected || !targetId) return;
        const controller = new AbortController();
        const offset = (ordersPage - 1) * ordersPageSize;
        setLoading(true);
        setError(null);
        shopifyApi.getLocalOrders(targetId, ordersPageSize, offset, {
            search: ordersSearch || undefined,
            financialStatus: ordersFinancialStatusFilter !== 'all' ? ordersFinancialStatusFilter : undefined,
            fulfillmentStatus: ordersFulfillmentStatusFilter !== 'all' ? ordersFulfillmentStatusFilter : undefined,
            tag: ordersTagFilter !== 'all' ? ordersTagFilter : undefined,
            sortBy: ordersSortBy,
            sortOrder: ordersSortOrder,
        })
            .then((res) => {
                if (controller.signal.aborted) return;
                setLocalOrders(Array.isArray(res.orders) ? res.orders : []);
                setTotalOrders(res.total ?? 0);
            })
            .catch((e: any) => {
                if (controller.signal.aborted) return;
                setError(friendlyMessage(e, "We couldn't load your orders. Please try again."));
            })
            .finally(() => {
                if (!controller.signal.aborted) { setLoading(false); markLoaded(); }
            });
        return () => controller.abort();
    }, [isConnected, scopeId, refreshKey, fetchKey, ordersPage, ordersPageSize, ordersSearch, ordersFinancialStatusFilter, ordersFulfillmentStatusFilter, ordersTagFilter, ordersSortBy, ordersSortOrder]);

    const loadLocalOrders = () => setFetchKey(k => k + 1);

    const hasActiveFilters = !!(ordersSearch || ordersFinancialStatusFilter !== 'all' || ordersFulfillmentStatusFilter !== 'all' || ordersTagFilter !== 'all');

    const clearAllFilters = useCallback(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = null;
        }
        setSearchInput('');
        setUrl({
            shopOrdSearch: '',
            shopOrdFinancial: '',
            shopOrdFulfillment: '',
            shopOrdTag: '',
            shopOrdPage: '',
        }, { replace: true });
    }, [setUrl]);

    useEffect(() => {
        if (isConnected && selectedAccountId) {
            loadOrderTags();
        }
    }, [isConnected, selectedAccountId]);

    useEffect(() => {
        if (!socket || !socketConnected || !socketBusinessAccountId) return;
        socket.emit('subscribe-business-account', socketBusinessAccountId);
        const handleOrderUpdated = (data: any) => {
            if (data.businessId && data.businessId !== socketBusinessAccountId) return;
            loadLocalOrders();
        };
        socket.on('shopify:order:updated', handleOrderUpdated);
        return () => {
            socket.off('shopify:order:updated', handleOrderUpdated);
            // Rooms are shared and counted (lib/socketAdapter.ts): each join needs its leave.
            socket.emit('unsubscribe-business-account', socketBusinessAccountId);
        };
    }, [socket, socketConnected, socketBusinessAccountId]);

    const handleCreateTag = async () => {
        if (!newTagName.trim() || !selectedAccountId) return;
        setCreateTagLoading(true);
        try {
            await shopifyApi.createOrderTag(selectedAccountId, newTagName.trim(), newTagIsDefault);
            toast({ variant: "success", title: 'Tag created', description: `"${newTagName.trim()}" added.` });
            setNewTagName('');
            setNewTagIsDefault(false);
            await loadOrderTags();
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Failed to create tag' } });
        } finally {
            setCreateTagLoading(false);
        }
    };

    const handleDeleteTag = async (tagId: string, tagName: string) => {
        if (!selectedAccountId) return;
        try {
            await shopifyApi.deleteOrderTag(selectedAccountId, tagId);
            toast({ variant: "success", title: 'Tag deleted', description: `"${tagName}" removed.` });
            await loadOrderTags();
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Failed to delete tag' } });
        }
    };

    const handleSetDefault = async (tagId: string) => {
        if (!selectedAccountId) return;
        setSettingDefaultTag(tagId);
        try {
            await shopifyApi.setDefaultOrderTag(selectedAccountId, tagId);
            toast({ variant: "success", title: 'Default tag set', description: 'Only this tag is now the default for this shop.' });
            await loadOrderTags();
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Failed to set default tag' } });
        } finally {
            setSettingDefaultTag(null);
        }
    };

    const handleUnsetDefault = async () => {
        if (!selectedAccountId) return;
        setSettingDefaultTag('unset');
        try {
            await shopifyApi.unsetDefaultOrderTag(selectedAccountId);
            toast({ variant: "success", title: 'Default tag cleared' });
            await loadOrderTags();
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Failed to clear default tag' } });
        } finally {
            setSettingDefaultTag(null);
        }
    };

    const handleOrderTagChange = async (orderId: string, tagValue: string) => {
        if (!selectedAccountId) return;
        setUpdatingTagForOrder(orderId);
        try {
            const newTags = tagValue === 'none' ? '' : tagValue;
            await shopifyApi.updateOrderTags(selectedAccountId, orderId, newTags);
            setLocalOrders(prev =>
                prev.map(o => o.id === orderId ? { ...o, tags: newTags } : o)
            );
        } catch (e: any) {
            showApiError(e, { fallback: { title: 'Failed to update status' } });
        } finally {
            setUpdatingTagForOrder(null);
        }
    };

    const ordersColumns: Column<typeof localOrders[0]>[] = useMemo(() => [
        {
            key: 'order',
            header: 'Order',
            render: (_, order) => (
                <span className="font-medium text-xs sm:text-sm">#{order.order_number || order.shopify_order_id || '—'}</span>
            ),
        },
        {
            key: 'customer',
            header: 'Customer',
            render: (_, order) => (
                <div className="space-y-0.5">
                    {order.email && <div className="text-xs sm:text-sm">{order.email}</div>}
                    {order.phone && <div className="text-xs text-muted-foreground">{order.phone}</div>}
                    {!order.email && !order.phone && <span className="text-xs text-muted-foreground">—</span>}
                </div>
            ),
        },
        {
            key: 'items',
            header: 'Items',
            render: (_, order) => {
                let lineItems = order.line_items;
                if (typeof lineItems === 'string') { try { lineItems = JSON.parse(lineItems); } catch { lineItems = []; } }
                if (!Array.isArray(lineItems)) lineItems = [];
                return lineItems.length > 0 ? (
                    <div className="text-xs sm:text-sm">
                        {lineItems.length} item{lineItems.length > 1 ? 's' : ''}
                        <div className="text-xs text-muted-foreground mt-0.5">{lineItems[0].title}{lineItems.length > 1 && ` +${lineItems.length - 1} more`}</div>
                    </div>
                ) : <span className="text-xs text-muted-foreground">—</span>;
            },
        },
        {
            key: 'status_tags',
            header: 'Status',
            render: (_, order) => {
                if (activeDomainTags.length === 0) {
                    const tags = order.tags ? String(order.tags).split(',').map((t: string) => t.trim()).filter(Boolean) : [];
                    return tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                            {tags.map((tag: string, i: number) => (
                                <Badge key={i} variant="secondary" className="text-xs capitalize">{tag}</Badge>
                            ))}
                        </div>
                    ) : <span className="text-xs text-muted-foreground">—</span>;
                }

                const currentTag = order.tags
                    ? String(order.tags).split(',').map((t: string) => t.trim()).find((t: string) =>
                        activeDomainTags.some(ot => ot.tag_name.toLowerCase() === t.toLowerCase())
                    ) || 'none'
                    : 'none';

                const isUpdating = updatingTagForOrder === order.id;
                return (
                    <div className="min-w-[120px]">
                        {isUpdating ? (
                            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        ) : (
                            <Select value={currentTag} onValueChange={(v) => handleOrderTagChange(order.id, v)}>
                                <SelectTrigger className="h-7 text-xs border-dashed">
                                    <SelectValue placeholder="Set status" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">
                                        <span className="text-muted-foreground">No status</span>
                                    </SelectItem>
                                    {activeDomainTags.map(tag => (
                                        <SelectItem key={tag.id} value={tag.tag_name}>
                                            <span className="flex items-center gap-1.5">
                                                {tag.is_default && <Star className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" />}
                                                {tag.tag_name}
                                            </span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'fulfillment_status',
            header: 'Fulfillment',
            render: (_, order) => order.fulfillment_status ? (
                <Badge variant="secondary" className="text-xs w-fit capitalize">{order.fulfillment_status}</Badge>
            ) : <span className="text-xs text-muted-foreground">Unfulfilled</span>,
        },
        {
            key: 'financial_status',
            header: 'Payment',
            render: (_, order) => order.financial_status ? (
                <Badge variant="outline" className="text-xs w-fit capitalize">{order.financial_status}</Badge>
            ) : <span className="text-xs text-muted-foreground">Pending</span>,
        },
        {
            key: 'total',
            header: 'Total',
            headerClassName: 'text-right',
            render: (_, order) => (
                <span className="text-right font-medium text-xs sm:text-sm block">
                    {order.currency} {parseFloat(order.total_price || 0).toFixed(2)}
                </span>
            ),
        },
        {
            key: 'date',
            header: 'Date',
            headerClassName: 'hidden md:table-cell',
            render: (_, order) => (
                <span className="text-xs text-muted-foreground hidden md:block">
                    {order.shopify_created_at ? new Date(order.shopify_created_at).toLocaleDateString() : '—'}
                </span>
            ),
        }
    ], [activeDomainTags, updatingTagForOrder]);

    if (!isConnected) {
        return (
            <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">Please connect your Shopify shop first to view orders.</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {error && <div className="text-xs text-destructive p-3 bg-destructive/10 rounded">{error}</div>}

            {selectedAccountId && (
                <div className="flex items-center justify-between gap-2 p-3 bg-muted/40 rounded-lg border">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Tag className="h-4 w-4 text-muted-foreground shrink-0" />
                        <span className="text-xs font-medium text-muted-foreground">Status Tags:</span>
                        {tagsLoading ? (
                            <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                        ) : activeDomainTags.length === 0 ? (
                            <span className="text-xs text-muted-foreground">No tags yet</span>
                        ) : (
                            activeDomainTags.map(tag => (
                                <Badge key={tag.id} variant={tag.is_default ? 'default' : 'secondary'} className="text-xs gap-1">
                                    {tag.is_default && <Star className="h-2.5 w-2.5 fill-current" />}
                                    {tag.tag_name}
                                </Badge>
                            ))
                        )}
                    </div>
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 shrink-0" onClick={() => { setShowTagDialog(true); setNewTagIsDefault(false); setNewTagName(''); }}>
                        <Plus className="h-3 w-3" />
                        Manage Tags
                    </Button>
                </div>
            )}

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 items-center justify-between">
                <div className="flex flex-1 gap-2 items-center w-full sm:w-auto flex-wrap">
                    <div className="relative flex-1 sm:max-w-[260px]">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search orders..."
                            value={searchInput}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="pl-9 h-9"
                        />
                        {searchInput && (
                            <X className="absolute right-2.5 top-2.5 h-4 w-4 text-muted-foreground cursor-pointer" onClick={() => handleSearchChange('')} />
                        )}
                    </div>

                    <Select value={ordersFinancialStatusFilter} onValueChange={(v) => setFilter('shopOrdFinancial', v)}>
                        <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Payment" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Payment</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="refunded">Refunded</SelectItem>
                            {ordersFinancialStatusFilter === 'paid_or_partial' && (
                                <SelectItem value="paid_or_partial" className="hidden">Paid (incl. partial)</SelectItem>
                            )}
                        </SelectContent>
                    </Select>

                    <Select value={ordersFulfillmentStatusFilter} onValueChange={(v) => setFilter('shopOrdFulfillment', v)}>
                        <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Fulfillment" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Fulfillment</SelectItem>
                            <SelectItem value="fulfilled">Fulfilled</SelectItem>
                            <SelectItem value="unfulfilled">Unfulfilled</SelectItem>
                            <SelectItem value="partial">Partial</SelectItem>
                            {ordersFulfillmentStatusFilter === 'not_fulfilled' && (
                                <SelectItem value="not_fulfilled" className="hidden">Pending shipment</SelectItem>
                            )}
                        </SelectContent>
                    </Select>

                    {activeDomainTags.length > 0 && (
                        <Select value={ordersTagFilter} onValueChange={(v) => setFilter('shopOrdTag', v)}>
                            <SelectTrigger className="w-[130px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Status</SelectItem>
                                {activeDomainTags.map(tag => (
                                    <SelectItem key={tag.id} value={tag.tag_name}>{tag.tag_name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <Select value={ordersSortBy} onValueChange={(v) => setFilter('shopOrdSortBy', v)}>
                        <SelectTrigger className="w-[110px] h-9"><SelectValue placeholder="Sort" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="shopify_created_at">Date</SelectItem>
                            <SelectItem value="total_price">Total</SelectItem>
                            <SelectItem value="order_number">Order #</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={ordersSortOrder} onValueChange={(v) => setFilter('shopOrdSortOrder', v)}>
                        <SelectTrigger className="w-[90px] h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="desc">Desc</SelectItem>
                            <SelectItem value="asc">Asc</SelectItem>
                        </SelectContent>
                    </Select>
                    {hasActiveFilters && (
                        <Button variant="ghost" size="sm" className="h-9 gap-1.5 text-xs text-muted-foreground hover:text-foreground" onClick={clearAllFilters}>
                            <X className="h-3.5 w-3.5" />
                            Clear filters
                        </Button>
                    )}
                </div>
            </div>

            <div className="rounded-md border">
                <DataTable
                    columns={ordersColumns}
                    data={localOrders}
                    currentPage={ordersPage}
                    pageSize={ordersPageSize}
                    totalItems={totalOrders}
                    onPageChange={(p) => setFilter('shopOrdPage', p > 1 ? String(p) : null, false)}
                    onPageSizeChange={(s) => setFilter('shopOrdPageSize', s !== 10 ? String(s) : null)}
                    loading={showListSkeleton({ loading, hasLoaded, willFetch: isConnected && !!scopeId })}
                    emptyMessage="No Shopify orders found. Try syncing."
                    showPagination={true}
                />
            </div>

            <Dialog open={showTagDialog} onOpenChange={setShowTagDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Tag className="h-4 w-4" />
                            Manage Order Status Tags
                        </DialogTitle>
                        {activeDomain && (
                            <p className="text-xs text-muted-foreground mt-1 font-mono bg-muted px-2 py-1 rounded w-fit">
                                {activeDomain}
                            </p>
                        )}
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-3 p-3 border rounded-lg bg-muted/20">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Create New Tag</p>
                            <div className="space-y-1.5">
                                <Label htmlFor="new-tag-name" className="text-sm">Tag Name</Label>
                                <Input
                                    id="new-tag-name"
                                    placeholder="e.g. Processing, Dispatched, On Hold"
                                    value={newTagName}
                                    onChange={(e) => setNewTagName(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); }}
                                    className="h-9"
                                />
                            </div>
                            {!activeDomainTags.some(t => t.is_default) && (
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="new-tag-default"
                                        checked={newTagIsDefault}
                                        onChange={(e) => setNewTagIsDefault(e.target.checked)}
                                        className="rounded border-gray-300 h-4 w-4 cursor-pointer"
                                    />
                                    <Label htmlFor="new-tag-default" className="text-sm cursor-pointer flex items-center gap-1.5">
                                        <Star className="h-3.5 w-3.5 text-yellow-500" />
                                        Set as default for this shop
                                    </Label>
                                </div>
                            )}
                            <Button
                                onClick={handleCreateTag}
                                disabled={!newTagName.trim() || createTagLoading}
                                size="sm"
                                className="w-full"
                            >
                                {createTagLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                                Create Tag
                            </Button>
                        </div>

                        <div className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Tags for this domain {activeDomainTags.length > 0 && `(${activeDomainTags.length})`}
                            </p>
                            {tagsLoading ? (
                                <div className="flex justify-center py-4">
                                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                                </div>
                            ) : activeDomainTags.length === 0 ? (
                                <p className="text-xs text-muted-foreground text-center py-4">No tags created yet for this shop.</p>
                            ) : (
                                <div className="space-y-1 max-h-52 overflow-y-auto">
                                    {activeDomainTags.map(tag => (
                                        <div key={tag.id} className="flex items-center justify-between p-2 rounded-md border bg-background">
                                            <div className="flex items-center gap-2">
                                                <Badge variant={tag.is_default ? 'default' : 'secondary'} className="text-xs">
                                                    {tag.tag_name}
                                                </Badge>
                                                {tag.is_default && (
                                                    <span className="text-xs text-yellow-600 font-medium flex items-center gap-1">
                                                        <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                                                        Default
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-1">
                                                {tag.is_default ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 text-yellow-500 hover:text-muted-foreground"
                                                        title="Remove default"
                                                        disabled={settingDefaultTag === 'unset'}
                                                        onClick={handleUnsetDefault}
                                                    >
                                                        {settingDefaultTag === 'unset'
                                                            ? <Loader2 className="h-3 w-3 animate-spin" />
                                                            : <Star className="h-3 w-3 fill-yellow-400" />
                                                        }
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 text-muted-foreground hover:text-yellow-500"
                                                        title="Set as default for this shop"
                                                        disabled={settingDefaultTag === tag.id}
                                                        onClick={() => handleSetDefault(tag.id)}
                                                    >
                                                        {settingDefaultTag === tag.id
                                                            ? <Loader2 className="h-3 w-3 animate-spin" />
                                                            : <Star className="h-3 w-3" />
                                                        }
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6 text-destructive hover:text-destructive"
                                                    onClick={() => handleDeleteTag(tag.id, tag.tag_name)}
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowTagDialog(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
